<script setup>
import { storeToRefs } from "pinia";
import { favoritesStore } from "@/stores/favoritesStore.js";
import { useI18n } from "vue-i18n";
import { computed, onMounted, ref } from "vue";
import BasePage from "@/components/Layouts/BasePage.vue";
import { ManageFavoritesEntity } from "@/entities/ManageFavoritesEntity.js";
import {icons} from "@/enums/enums.js";

const { t } = useI18n();
const favStore = favoritesStore();
const { favoriteCards } = storeToRefs(favStore);

const draggingIndex = ref(null);
const entity = ref(new ManageFavoritesEntity());

const favorites = computed(() => favoriteCards.value);

onMounted(() => {
    favStore.loadFavorites();
});

function onDragStart(index) {
    draggingIndex.value = index;
}

async function onDrop(dropIndex) {
    if (draggingIndex.value === null) return;

    const list = [...favorites.value];
    const draggedItem = list.splice(draggingIndex.value, 1)[0];
    list.splice(dropIndex, 0, draggedItem);

    draggingIndex.value = null;
    await favStore.updateOrder(list);
}

async function selectIcon(fav, icon) {
    fav.icon = icon;
    await favStore.updateIcon(fav.title, icon);
}

async function removeFavorite(fav) {
    await favStore.toggleFavorite(fav);
}

</script>
<template>
    <BasePage :entity="entity">
        <div v-if="!favorites.length" class="text-center py-10">
            {{ t("common.labels.noData") }}
        </div>

        <div v-else class="d-flex flex-wrap gap-4">
            <v-card
                v-for="(fav, index) in favorites"
                :key="fav.title"
                width="280"
                class="pa-3 cursor-move v-card--hover elevation-1 bg-primary-50 me-5 mb-5"
                draggable="true"
                @dragstart="onDragStart(index)"
                @dragover.prevent
                @drop="onDrop(index)"
            >
                <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="danger"
                    class="position-absolute top-0 right-0"
                    @click.stop="removeFavorite(fav)"
                >
                    <v-icon class="mt-2 me-2" color="red" size="25">mdi-delete</v-icon>
                </v-btn>
                <div class="d-flex align-center gap-2">
                    <v-menu location="bottom">
                        <template #activator="{ props }">
                            <v-icon
                                v-bind="props"
                                size="20"
                                class="cursor-pointer text-dark me-2"
                                :title="t('common.labels.changeIcon')"
                            >
                                {{ fav.icon || "mdi-star" }}
                            </v-icon>
                        </template>

                        <v-sheet class="pa-2 bg-white" width="280">
                            <div class="d-flex flex-wrap gap-2">
                                <v-btn
                                    v-for="icon in icons"
                                    :key="icon"
                                    icon
                                    variant="text"
                                    @click="selectIcon(fav, icon)"
                                >
                                    <v-icon >{{ icon }}</v-icon>
                                </v-btn>
                            </div>
                        </v-sheet>
                    </v-menu>
                    <span class="text-truncate text-dark">
                        {{ t(fav.title) }}
                    </span>
                </div>
            </v-card>
        </div>
    </BasePage>
</template>
